import { generateGUID, getVuukleLogo } from '~/utils/general';

/**
 * Static class with methods to create notification alerts
 */
class NotificationAlert {
  /** If notification corner exists or not */
  private static cornerCreated = false;
  /** Notification corner element */
  private static notificationCorner: HTMLDivElement;
  /** Texts array of created notifications */
  private static createdNotifications: string[] = [];

  /** Retrieves an existing notification corner or creates one */
  private static createNotificationCorner() {
    const existingCorner: HTMLDivElement | null = document.getElementById('vuukle_alerts_fixed_left') as HTMLDivElement;
    if (existingCorner) {
      NotificationAlert.cornerCreated = true;
      NotificationAlert.notificationCorner = existingCorner;
    } else {
      const notificationCorner = document.createElement('div');
      notificationCorner.id = 'vuukle_alerts_fixed_left';
      notificationCorner.style.cssText = `
        position: fixed;
        bottom: 10px;
        right: -16px;
        borderRadius: 5px;
        display: flex;
        flex-direction: column-reverse;
        height: 100vh;
        overflow-y: auto;
        pointer-events: none;
        align-items: flex-end;
        z-index: 2147483648;
        color: #000;
      `;
      NotificationAlert.cornerCreated = true;
      NotificationAlert.notificationCorner = notificationCorner;
      document.body.appendChild(notificationCorner);
    }
  }

  /** Styling for the notification container */
  private static alertContainerStyles = `
    padding: 12px 16px;
    overflow: hidden;
    display: flex;
    position: relative;
    font-family: -apple-system,system-ui,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',sans-serif;
    font-weight: 500;
    box-shadow: rgb(67 90 111 / 30%) 0px 0px 1px, rgb(67 90 111 / 47%) 0px 8px 10px -4px;
    margin-right: 10px;
    margin-bottom: 15px;
    border-left: 5px solid #909399;
    align-items: center;
    background: #fff;
    pointer-events: auto;
  `;

  /** Style for alert text container */
  private static alertTextContainerStyles = `
    flex: 1 1 0%;
  `;

  /** Styling for alert text */
  private static alertTextStyles = `
    font-weight: 600px;
    line-height: 20px;
    margin-bottom: 0;
    margin-top: 0;
    letter-spacing: -0.05px;
    font-size: 14px;
  `;

  /** Styling for alert remove button */
  private static alertRemoveButtonStyles = `
    transform: rotate(45deg);
    margin-left: 10px;
    cursor: pointer;
    font-size: 26px;
  `;

  /** Color table for alert types */
  private static alertColors = {
    error: '#f56c6c',
    info: '#909399',
    success: '#67c23a',
    warning: '#e6a23c',
  };

  private static notificationLogoHTML = `
    <a href="https://vuukle.com" target="_blank" rel="noopener nofollow" style="margin-right: 5px;">
      ${getVuukleLogo(30, '0 0 30 30')}
    </a>
  `;

  /**
   * @param text
   * @param type
   * @param duration
  /** Creates an alert notification
   */
  public static createNotification(
    text: string,
    type: keyof typeof NotificationAlert.alertColors,
    duration: number = 10000
  ) {
    if (this.createdNotifications.includes(text)) {
      return;
    } else {
      this.createdNotifications.push(text);
    }

    if (!NotificationAlert.cornerCreated) {
      // create or retrieve the notification corner
      NotificationAlert.createNotificationCorner();
    }

    // initialize the elements
    const notificationDiv = document.createElement('div');
    const notificationLogoContainer = document.createElement('div');
    const notificationTextContainer = document.createElement('div');
    const notificationText = document.createElement('h4');
    const notificationRemoveContainer = document.createElement('div');
    const notificationRemoveButton = document.createElement('span');

    // give the alert a unique id
    notificationDiv.id = `alert-${generateGUID()}`;

    // give the elements of the alert their styling
    notificationDiv.style.cssText = NotificationAlert.alertContainerStyles;
    notificationDiv.style.borderLeft = `5px solid ${NotificationAlert.alertColors[type]}`;
    notificationTextContainer.style.cssText = NotificationAlert.alertTextContainerStyles;
    notificationText.style.cssText = NotificationAlert.alertTextStyles;
    notificationRemoveContainer.style.cssText = NotificationAlert.alertRemoveButtonStyles;

    // set logo html
    notificationLogoContainer.style.display = 'flex';
    notificationLogoContainer.style.alignItems = 'center';
    notificationLogoContainer.innerHTML = NotificationAlert.notificationLogoHTML;

    // set alert text
    notificationTextContainer.innerHTML = text;

    // set up the alert remove button
    notificationRemoveContainer.textContent = '+';
    notificationRemoveContainer.setAttribute('aria-label', 'Close Alert');
    notificationRemoveContainer.setAttribute('role', 'button');
    notificationRemoveContainer.setAttribute('title', 'Close Alert');
    notificationRemoveContainer.onclick = () => NotificationAlert.removeNotificationById(notificationDiv.id, text);

    // put the parts of alert notification together
    notificationTextContainer.appendChild(notificationText);
    notificationRemoveContainer.appendChild(notificationRemoveButton);
    notificationDiv.appendChild(notificationLogoContainer);
    notificationDiv.appendChild(notificationTextContainer);
    notificationDiv.appendChild(notificationRemoveContainer);

    // append the notification to the corner
    NotificationAlert.notificationCorner.appendChild(notificationDiv);

    // Set an autoremoval timeout
    setTimeout(() => {
      NotificationAlert.removeNotificationById(notificationDiv.id, text);
    }, duration);
  }

  /**
   * @param id
   * @param text
/** Removes an alert notification
   */
  public static removeNotificationById(id: string, text: string) {
    this.createdNotifications = this.createdNotifications.filter((notify) => notify !== text);
    const notificationItem = document.getElementById(id);
    if (notificationItem && notificationItem.parentNode) {
      notificationItem.parentNode.removeChild(notificationItem);
    }
  }
}

export default NotificationAlert;
